// whitelabelBrand.js — the PRE-AUTH half of the white-label chain
// (WHITELABEL-BRANDED-BOOT-1, tickets/opus.md).
//
// ── Why this is separate from customDomainBoot.js ────────────────────────
// `bootCustomDomainScope` (customDomainBoot.js) answers "which /w/<org>/<ws>
// URL does this host mean" and is POST-AUTH by construction: it waits for
// `root.orgs`, which `loadPrivateData` only populates once a user is signed
// in. Branding has the opposite requirement — the branded LOADER paints
// during boot and the branded LOGIN paints for a logged-OUT visitor, so it
// must resolve before any authenticated fetch exists. Same endpoint, same
// fail-closed contract, different moment; merging them would deadlock one or
// the other. The scope route is deliberately public and unauthenticated for
// exactly this reason (server organizations.js:31).
//
// ── Never hardcode ───────────────────────────────────────────────────────
// Every visual here comes from the org's `branding` record returned by that
// endpoint. Nothing in this file (or its consumers) knows what "bellforge"
// is — the same code serves the next white-label org with a record and no
// deploy. Contract, all display-only (the server projects away persona user
// ids and omits `personaSignins` unless the org enables them):
//   { name, tagline, logoSvg, loaderLogoSvg, accent, background, theme,
//     personaSignins: [{ key, label }] }
//
// Plus the SIBLING scope fields this lookup carries through so consumers need
// no second request: `workspaceKey`, `organizationId`, `workspaceId`, and
// `workspaceLanguage: { language, languagePolicy }` — the workspace's
// enforced-language pair, read by `enforcedWorkspaceLang`.
//
// ⚠️ A host with an enforced language but NO presentational branding gets
// `null` from the guard below and therefore no pre-auth enforcement. That is
// the pre-existing "a record with nothing to show is not a brand" contract,
// and it costs nothing real: a branded host has branding by definition, and
// the live workspace record enforces the same language a step later.
//
// ── Bounded, and never a boot gate ───────────────────────────────────────
// `seedWhitelabelBrand` races a short timeout so a slow/dead lookup can
// never delay the mount — the exact hazard themeMode.js's `resolveBootThemeMode`
// documents (trading a cosmetic default for a blank page). Whatever misses
// the race still lands via `applyWhitelabelBrandLate` on the live root, and
// every consumer reads it reactively, so a late brand simply swaps in.
// Platform hosts never fetch at all: byte-identical to today.
import _wsShared from '@symbo-ls/workspace-shared'
import { isCustomDomainHost, isPlatformHost } from './customDomainHost.js'
import { setHostScopeOrgId } from './hostScopePin.js'

// Read through the shared package at CALL time, never captured at module
// scope (customDomainBoot.js's idiom): the barrel's default is not safe to
// destructure while the shared-barrel init cycle runs.
const shellSetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.setActiveWorkspacePointer(...args)
const shellResolveLang = (...args) => _wsShared.functions.resolveLang(...args)
const shellEnforcedWorkspaceLang = (...args) => _wsShared.functions.enforcedWorkspaceLang(...args)

// One in-flight request per page load. The brand and the host's TENANT are
// two halves of the SAME response, so every consumer below (the brand seed,
// the late apply, the tenant seed) shares this one promise.
let _lookupPromise = null
// The brand half, with its once-per-load side effects (boot identity cache,
// favicon) applied exactly once however many consumers ask.
let _brandPromise = null

const NO_LOOKUP = Object.freeze({ scope: null, brand: null })

/**
 * The TENANT a white-label host is bound to, from the scope payload, or null.
 *
 * WHITELABEL-STRICT-TENANT-SCOPE-1 — why this is NOT read off the brand.
 * The tenant ids used to ride along on the brand object only, and the brand
 * is null for an org with no presentational record ("a record with nothing to
 * show is not a brand", below). So every UNBRANDED white-label host lost its
 * tenant: nothing scoped the boot snapshot or the auth tick, and the shell
 * booted the account's ambient org under the customer's domain. Measured on
 * unreal.pr.symbols.app 2026-10-15: the Unreal org has `branding: {}`, the
 * signed-in account's stored active org was Keepz, and /studio drew Keepz's
 * projects. Branding is presentation; the tenant is isolation. One must never
 * depend on the other.
 *
 * Both ids are required: the server resolves a host only through a verified
 * row that names a workspace, so a payload without both is not a binding.
 */
const scopeFromPayload = (data) => {
  if (!data || typeof data !== 'object') return null
  if (!data.organizationId || !data.workspaceId) return null
  return {
    organizationId: String(data.organizationId),
    workspaceId: String(data.workspaceId),
    workspaceKey: data.workspaceKey ? String(data.workspaceKey) : null,
    domain: data.domain ? String(data.domain) : null
  }
}

/**
 * Raw lookup, both halves: `{ scope, brand }`. Either half is null for "none
 * here" — which covers every failure: platform host, no row, unverified
 * domain, network error, malformed body. A white-label surface with no record
 * renders the generic Symbols chrome (the brand half), and a host with no
 * resolvable tenant renders NO tenant (the scope half, see
 * seedWhitelabelHostScope) — both are the correct fallbacks.
 */
export const fetchWhitelabelLookup = async (opts = {}) => {
  const hostname = opts.hostname
  const apiBase = String(opts.apiBase || '').replace(/\/+$/, '')
  const fetchImpl = opts.fetchImpl
  if (!hostname || !apiBase || typeof fetchImpl !== 'function') return NO_LOOKUP
  try {
    const url = `${apiBase}/core/organizations/custom-domain-scope?host=${encodeURIComponent(hostname)}`
    // `credentials: 'omit'` — this runs for logged-out visitors and must not
    // depend on (or leak) a session. Same call shape customDomainBoot uses.
    const res = await fetchImpl(url, { credentials: 'omit' })
    if (!res || !res.ok) return NO_LOOKUP
    const body = await res.json()
    const data = body && body.data
    return { scope: scopeFromPayload(data), brand: brandFromPayload(data) }
  } catch (_) {
    return NO_LOOKUP
  }
}

/**
 * The brand half of the lookup — the long-standing contract: the branding bag
 * (plus the sibling scope fields the branded surfaces read), or null.
 */
export const fetchWhitelabelBrand = async (opts = {}) => (await fetchWhitelabelLookup(opts)).brand

// The scope payload's `data` → the brand bag, or null. Pure; never throws
// past its own guard.
const brandFromPayload = (data) => {
  try {
    const brand = data && data.branding
    if (!brand || typeof brand !== 'object') return null
    // A record with nothing to show is not a brand — treat it as none so
    // consumers can gate on truthiness alone.
    if (!brand.name && !brand.logoSvg && !brand.loaderLogoSvg) return null
    // Carry the resolved workspace key alongside the brand. It is a sibling
    // of `branding` in the scope payload, and the branded surfaces need it to
    // name a workspace to the public onboarding endpoint — without it they
    // would have to re-request the very response we already hold. Attached
    // only when the server sent one, so an older server (or any payload
    // without it) yields exactly the brand object it always did.
    const wsKey = data.workspaceKey
    // WS-WHITELABEL-TENANT-SCOPE-1 — carry the resolved TENANT ids too.
    //
    // `organizationId` / `workspaceId` are already in this very response (they
    // are what `resolveCustomDomainScope` reads from it), and this lookup is
    // already awaited before bootShell runs. Passing them through turns the
    // host's tenant into something the BOOT SNAPSHOT can be scoped by, with no
    // second request and no ladder to race — which is the difference between
    // preventing the wrong-tenant render and correcting it afterwards.
    //
    // Attached only when the server sent them, so an older server yields
    // exactly the brand object it always did, and `scopeSnapshotToWhitelabelHost`
    // treats a missing `organizationId` as "no scope, change nothing".
    const orgId = data.organizationId
    const wsId = data.workspaceId
    // WORKSPACE-ENFORCED LANGUAGE, PRE-AUTH — `{ language, languagePolicy }`,
    // another sibling of `branding` in this same response. It is the ONLY way
    // a branded host can honour an enforced language from FIRST PAINT: the
    // workspace record itself does not exist until after sign-in, so without
    // this the page boots into the visitor's own language and re-languages
    // itself a step later. Read by `enforcedWorkspaceLang` (workspace-shared
    // functions/languages.js), the same function that reads a live
    // `workspace.settings` — the shapes match on purpose.
    //
    // NOT the same thing as `brand.lang`, which is the ORG's language and the
    // WEAKEST signal in the resolver. This one is a rule; that one is a
    // fallback for a visitor nothing else can speak for.
    //
    // Carried verbatim (an object, not a derived code) so the one decision
    // stays in one function rather than being half-made here.
    const wsLang = data.workspaceLanguage
    const extra = {}
    if (wsKey) extra.workspaceKey = String(wsKey)
    if (orgId) extra.organizationId = String(orgId)
    if (wsId) extra.workspaceId = String(wsId)
    if (wsLang && typeof wsLang === 'object') extra.workspaceLanguage = wsLang
    return Object.keys(extra).length ? { ...brand, ...extra } : brand
  } catch (_) {
    return null
  }
}

/**
 * Start (once) the ONE lookup for THIS page load. Safe to call at module-eval
 * time from the entry: it kicks the request off in parallel with SDK init
 * and the auth round-trip instead of behind them. Resolves `{ scope, brand }`
 * and never rejects.
 */
const kickWhitelabelLookup = (opts = {}) => {
  if (_lookupPromise) return _lookupPromise
  const win = opts.win || (typeof window !== 'undefined' ? window : null)
  if (!win || !win.location) return (_lookupPromise = Promise.resolve(NO_LOOKUP))
  // The canonical host test, with its documented local overrides
  // (`localStorage.symbols_force_custom_domain='1'` / `?customDomain=1`) so a
  // brand can be exercised against a local shell — customDomainHost.js.
  const onCustomDomain = opts.forceCustomDomain === true || isCustomDomainHost()
  if (!onCustomDomain) return (_lookupPromise = Promise.resolve(NO_LOOKUP))
  _lookupPromise = fetchWhitelabelLookup({
    hostname: win.location.hostname,
    apiBase: opts.apiBase,
    fetchImpl: opts.fetchImpl || (typeof win.fetch === 'function' ? win.fetch.bind(win) : null)
  }).catch(() => NO_LOOKUP)
  return _lookupPromise
}

/**
 * The brand half of this page load's lookup (null for "no brand here").
 */
export const kickWhitelabelBrand = (opts = {}) => {
  if (_brandPromise) return _brandPromise
  const win = opts.win || (typeof window !== 'undefined' ? window : null)
  _brandPromise = kickWhitelabelLookup(opts)
    .then(({ brand }) => {
      cacheWhitelabelBootIdentity(brand, win)
      // The one place BOTH consumers (seed and late apply) share, so the
      // tab icon lands exactly once per page load, however the race went.
      applyWhitelabelFavicon(brand, win)
      return brand
    })
    .catch(() => null)
  return _brandPromise
}

/**
 * The tenant half of this page load's lookup (null for "no tenant resolved").
 * Same single request as the brand — never a second fetch.
 */
export const kickWhitelabelHostScope = (opts = {}) =>
  kickWhitelabelLookup(opts)
    .then((lookup) => (lookup && lookup.scope) || null)
    .catch(() => null)

/**
 * WHITELABEL-STRICT-TENANT-SCOPE-1 — bind THIS tab to the host's tenant
 * BEFORE bootShell runs, or bind it to nothing.
 *
 * Writes, on a white-label host only (a platform host returns null and writes
 * nothing — byte-identical to before):
 *   - `contextState.whitelabelHostScope` — the scope `{ organizationId,
 *     workspaceId, workspaceKey, domain }`, or `false` when this is a real
 *     customer host whose tenant could NOT be established (no verified row,
 *     transport failure, timeout). `false` is read downstream as "render no
 *     tenant at all": a customer's domain that cannot prove which tenant it
 *     serves must not fall back to the account's ambient one.
 *   - the auth pin (hostScopePin.js) — so the very first auth tick already
 *     stands down instead of resolving the account's stored active org;
 *   - the tab-side workspace pointer — so the boot's own precedence (pointer
 *     → server claim) picks the host's workspace. Origin-scoped storage: no
 *     my.symbols tab can move.
 *
 * NOT raced against a short timeout like the brand seed. The brand is
 * cosmetic and may land late; the tenant is the isolation boundary, and a
 * shell mounted before it is known is exactly the wrong-tenant render this
 * prevents. The bound below only stops a dead network from hanging the boot
 * forever — and its outcome is `false`, never "unscoped".
 *
 * The documented local simulation (`symbols_force_custom_domain` /
 * `?customDomain=1` on a PLATFORM hostname) keeps its old meaning: a missing
 * scope there leaves the boot unscoped, because the real hostname is one of
 * Symbols' own and no customer tenant is at stake.
 */
export const seedWhitelabelHostScope = async (contextState, opts = {}) => {
  const win = opts.win || (typeof window !== 'undefined' ? window : null)
  const onCustomDomain = opts.forceCustomDomain === true || isCustomDomainHost()
  if (!win || !win.location || !onCustomDomain) return null
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 15000
  const TIMED_OUT = Symbol('whitelabelHostScopeTimeout')
  let timer = null
  const raced = await Promise.race([
    kickWhitelabelHostScope(opts),
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs)
    })
  ])
  if (timer) clearTimeout(timer)
  const scope = raced && raced !== TIMED_OUT ? raced : null
  const realCustomerHost = !isPlatformHost(win.location.hostname || '')
  if (!scope && !realCustomerHost) return null
  if (contextState) {
    contextState.whitelabelHostScope = scope || false
    contextState._hostScopeOrgId = scope ? scope.organizationId : null
  }
  setHostScopeOrgId(scope && scope.organizationId)
  if (scope && scope.workspaceId) {
    try {
      shellSetActiveWorkspacePointer(scope.workspaceId)
    } catch (_) {}
  }
  return scope
}

/**
 * Theme the favicon markup for the TAB BAR, not the page (Nika 2026-08-31:
 * "favicon for bellforge should be light on dark mode and dark on light
 * mode"). The record's mark draws with `stroke="currentColor"`, and a
 * favicon is a STANDALONE SVG document — no page sets `color`, so
 * currentColor resolved to the UA initial (black): invisible on a dark tab
 * strip. Injecting one `<style>` keyed on prefers-color-scheme makes the
 * ONE file adapt: glyph = the record's DARK ground on a light tab strip,
 * its LIGHT ground on a dark one (both fall back to neutral inks). Chrome,
 * Safari and Firefox all evaluate the media query inside SVG favicons.
 * A record svg with fixed colors ignores the style — harmless.
 */
export const themedFaviconSvg = (icon, brand) => {
  if (typeof icon !== 'string' || !/^\s*<svg\b/i.test(icon)) return icon
  const lightGlyph = (brand && brand.background) || '#18181b'
  const darkGlyph = (brand && brand.backgroundLight) || '#f4f4f5'
  const style =
    `<style>:root{color:${lightGlyph}}` +
    `@media (prefers-color-scheme: dark){:root{color:${darkGlyph}}}</style>`
  return icon.replace(/(<svg\b[^>]*>)/i, `$1${style}`)
}

/**
 * Swap the tab icon to the org's own mark (WHITELABEL-FAVICON, Nika
 * 2026-08-30: "bellforge workspace should have bellforge in favicon").
 *
 * Replaces every `link[rel="icon"]` rather than editing the first one:
 * index.html ships a static Symbols icon link, and browsers prefer the
 * LATER link, so an edited-in-place early link would lose to it. The data:
 * URI keeps this a pure client-side swap — no per-tenant asset upload, no
 * hardcoded URL, the same never-hardcode rule the rest of this file obeys.
 *
 * Best-effort like everything here: no document, a brandless host, or a
 * non-SVG payload all return false and change nothing — the Symbols icon
 * is the correct fallback, not a degraded one.
 */
export const applyWhitelabelFavicon = (
  brand,
  win = typeof window !== 'undefined' ? window : null
) => {
  try {
    if (!win || !win.document) return false
    const icon = brand && (brand.logoSvg || brand.loaderLogoSvg)
    if (!icon || typeof icon !== 'string' || !/^\s*<svg\b/i.test(icon)) return false
    const doc = win.document
    const stale = doc.querySelectorAll('link[rel="icon"]')
    for (let i = 0; i < stale.length; i++) {
      const l = stale[i]
      if (l && l.parentNode) l.parentNode.removeChild(l)
    }
    const link = doc.createElement('link')
    link.rel = 'icon'
    link.type = 'image/svg+xml'
    link.href =
      'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(themedFaviconSvg(icon, brand))
    ;(doc.head || doc.documentElement).appendChild(link)
    return true
  } catch (_) {
    return false
  }
}

/**
 * Persist just enough of the brand for the NEXT boot to paint correctly
 * before any network call.
 *
 * ⚠️ This is what makes the branded boot screen possible at all. The
 * full-viewport `#boot-spiral` in index.html paints on the first byte and is
 * only lifted once the whole data + module load finishes — on a workspace
 * whose module needs 17-45s to prepare, that is the loader the user actually
 * stares at, and it was hardcoded to the Symbols spiral on a Symbols ground.
 * It cannot read root state and it cannot await a fetch without painting the
 * wrong thing first, so the only correct answer is a synchronous read of what
 * we learned LAST time. index.html's head script consumes exactly this.
 *
 * HOST-KEYED on purpose: one browser can visit several white-label tenants,
 * and a shared key would paint one tenant's colours on another's domain.
 *
 * Best-effort by construction — a private-mode or quota-full localStorage
 * must never break a boot, so every failure is swallowed and the next boot
 * simply falls back to the generic screen.
 */
export const cacheWhitelabelBootIdentity = (
  brand,
  win = typeof window !== 'undefined' ? window : null
) => {
  try {
    if (!brand || !win || !win.location || !win.localStorage) return false
    const isLight = brand.theme === 'light'
    // Ink is chosen here rather than stored per-tenant: the boot screen shows
    // a mark and nothing else, so it only needs to be legible on the ground,
    // and these are the same two values the non-branded boot screen uses.
    const payload = {
      theme: brand.theme === 'light' || brand.theme === 'dark' ? brand.theme : null,
      bg: (isLight ? brand.backgroundLight : brand.background) || null,
      ink: isLight ? '#18181b' : '#f4f4f5',
      // The loader mark first — it is the one drawn for exactly this purpose.
      logo: brand.loaderLogoSvg || brand.logoSvg || null,
      // The tab icon wants the COMPACT mark, so precedence flips: logoSvg
      // (the mark the sign-in card draws) before the loader variant.
      // Cached ALREADY THEMED (themedFaviconSvg) so index.html's head
      // script stays a dumb applier — one transform site, no drift.
      icon: themedFaviconSvg(brand.logoSvg || brand.loaderLogoSvg || null, brand),
      name: brand.name || null,
      // THE BOOT MOTION, same OPT-IN law the in-app MainLoader applies
      // (components/whitelabel/MainLoader.js): only `loaderMotion === 'dial'`
      // takes the combination-dial lockup, every other value and the absent
      // key keep the coin flip (Nika 2026-09-10: "change loading spinner only
      // for vault - keep bellforge and gita as was"). Normalised to
      // 'dial' | null HERE so index.html's head script stays a dumb applier:
      // one branch on a cached string, no logic beyond a class toggle.
      // Written on EVERY boot, so a cache left by an older shell, or by a
      // record whose motion has since changed, heals on the next load.
      motion: brand.loaderMotion === 'dial' ? 'dial' : null,
      // THE BOOT LANGUAGE for this HOST (WS-BOOT-LOADER-PAINTS-ENGLISH-ONLY-
      // BEFORE-THE-BUNDLE-1). Same reason as `theme` above: the pre-bundle
      // overlay in index.html cannot await a lookup without painting the
      // wrong language first, so it reads back what this boot learned.
      //
      // RESOLVED here, not raw, so the decision stays in ONE function —
      // `resolveLang` (workspace-shared functions/languages.js), fed the two
      // signals this record carries: the host's pre-auth workspace pair
      // (`workspaceLanguage`, the strong one) and the org's own `lang` (the
      // weakest level). index.html stays a dumb applier, exactly like it is
      // for the theme, the motion and the icon.
      //
      // Host-keyed, so one tenant's language can never paint another's
      // domain — and this is the HOST's level of the precedence, below the
      // per-workspace entry functions/bootLang.js writes. A workspace that
      // sets its own language still wins there.
      lang: (() => {
        const pair = brand.workspaceLanguage || null
        const resolved = shellResolveLang({
          workspaceEnforced: shellEnforcedWorkspaceLang(pair),
          workspaceDefault: (pair && pair.language) || null,
          brandDefault: brand.lang || null
        })
        return resolved || null
      })()
    }
    win.localStorage.setItem('wlBoot:' + win.location.hostname, JSON.stringify(payload))
    return true
  } catch (_) {
    return false
  }
}

/**
 * Bounded seed onto the PRE-MOUNT `context.state`, so a brand that resolves
 * in time is present at FIRST paint (no generic-then-branded flash for the
 * common case). Returns the brand it seeded, or null.
 *
 * ⛔ The timeout is the whole safety story — see the file header. On timeout
 * the boot continues generic and `applyWhitelabelBrandLate` still swaps the
 * brand in when it lands.
 */
export const seedWhitelabelBrand = async (contextState, opts = {}) => {
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 600
  const promise = kickWhitelabelBrand(opts)
  const TIMED_OUT = Symbol('whitelabelBrandTimeout')
  const brand = await Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), timeoutMs))
  ])
  if (brand === TIMED_OUT || !brand) return null
  if (contextState) contextState.whitelabelBrand = brand
  return brand
}

/**
 * Second chance: write the brand onto the LIVE root once the lookup settles,
 * for the loads where it lost the race above. `root.update` is the reactive
 * write every consumer's factory tracks, so the loader/login re-render
 * themselves. No-op when there is no brand, or when the seed already landed
 * it (same value, and consumers are idempotent).
 */
export const applyWhitelabelBrandLate = (root, opts = {}) => {
  if (!root || typeof root.update !== 'function') return Promise.resolve(null)
  return kickWhitelabelBrand(opts)
    .then((brand) => {
      if (!brand) {
        // SETTLED WITHOUT A BRAND, on a custom-domain host: `false`, not
        // `null`, so a reader can tell "no brand here" from "still loading"
        // (functions/authWrapperTitle.js gives the sign-in wrappers their own
        // title back instead of waiting for a brand that never comes). Every
        // existing reader tests truthiness, so false reads like null to them.
        const onCustom = opts.forceCustomDomain === true || isCustomDomainHost()
        if (onCustom && root.whitelabelBrand == null) {
          root.update({ whitelabelBrand: false }, { preventFetch: true })
        }
        return null
      }
      if (root.whitelabelBrand) return root.whitelabelBrand
      root.update({ whitelabelBrand: brand }, { preventFetch: true })
      return brand
    })
    .catch(() => null)
}

// Test seam only — the module-scope cache is what makes the seed and the
// late apply share ONE request; tests need to reset it between cases.
export const _resetWhitelabelBrandCache = () => {
  _lookupPromise = null
  _brandPromise = null
}
